// A publisher's own discussion of an article, in one normalized shape (docs/design/discussion-plan.md).
// Adapters on the backend turn a provider's page or API into [RawComment]s; [threadComments] makes the
// threads both clients draw (discussionView.ts), so ordering, nesting, caps and paging are written once.
// Comment text is a small tree of runs, never HTML: nothing here or in the view can carry a script.

/** Inline text: plain, emphasised, strong, code, or a link (http and https only; see [safeHref]). */
export type Run = string | { i: Run[] } | { b: Run[] } | { code: string } | { a: Run[]; href: string };
/** A paragraph is a line of runs; a body is paragraphs. */
export type Body = Run[][];
/** What a comment quotes (a forum's quoted post): kept as a quote, never taken for a parent. */
export interface Quote { by?: string; text: string }

/** A comment as a provider gives it: in source order, with its parent when the provider has parents. */
export interface RawComment {
  id: string;
  parent?: string;
  by: string;
  at?: number;
  score?: number;
  /** only a label the provider states ("Writer", "Moderator"); never guessed from a name */
  tag?: string;
  quote?: Quote;
  body: Body;
  /** this comment on the publisher's site */
  link?: string;
  removed?: boolean;
  /** the site itself hides it (a comment below its score threshold): kept like a removed one, with its reason */
  hidden?: boolean;
}

/** A comment as drawn. Roots carry every reply under them, flat in source order. */
export interface Comment {
  id: string;
  by: string;
  at?: number;
  score?: number;
  tag?: string;
  quote?: Quote;
  body: Body;
  link?: string;
  /** a place held for a removed (or empty) comment that has replies */
  removed?: true;
  /** ... held because the site hides it, not because it is gone */
  hidden?: true;
  /** whom a reply answers, when that is not the root of its thread */
  to?: string;
  replies?: Comment[];
}

/** What a request for a discussion comes back as. `none`: no discussion is known; nothing is shown. */
export type DiscussionState = "ok" | "empty" | "closed" | "blocked" | "failed" | "none";
export interface DiscussionPage {
  state: DiscussionState;
  /** every comment on the article, replies included, as the provider counts them */
  total?: number;
  threads: Comment[];
  /** opaque: pass it back to get the next page of conversations */
  next?: string;
  /** conversations after this page */
  left?: number;
  /** the discussion on the publisher's site */
  link: string;
  fetchedAt: number;
}

/** At most this many conversations (top-level comments) arrive in a page, and this many bytes of JSON. */
export const ROOTS_PER_PAGE = 25;
export const PAGE_BYTES = 100_000;
/** At most this much text in one comment; the rest is cut with an ellipsis. */
export const COMMENT_CHARS = 4096;

import publishers from "./publishers.json";
import { DISCUSSION_KEEP_MS, discussionLifetimes } from "./discussionPolicy";

export const discussionKeepMs = (state: string): number => discussionLifetimes[state] ?? DISCUSSION_KEEP_MS;

/** Cut text without leaving half of an emoji at the boundary. */
export function cutText(text: string, max: number): string {
  const cut = text.slice(0, Math.max(0, max));
  return /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut;
}

const hostOf = (url: string): string => { try { return new URL(url).hostname.replace(/^www\./, "").toLowerCase(); } catch { return ""; } };

export function discussionPublisher(address: string) {
  try {
    const url = new URL(address);
    if (!/^https?:$/.test(url.protocol)) return undefined;
    return publishers.find(p => p.host === url.hostname.replace(/^www\./, "") && url.pathname.startsWith(p.path));
  } catch { return undefined; }
}

export const hasDiscussion = (url: string, kind: string): boolean => discussionPublisher(url)?.kinds.includes(kind) ?? false;

export function discussionLink(address: string): string {
  const publisher = discussionPublisher(address);
  try {
    const url = new URL(address);
    if (publisher?.suffix) {
      if (!url.pathname.endsWith(publisher.suffix)) url.pathname = `${url.pathname.replace(/\/$/, "")}/${publisher.suffix}`;
      url.hash = "";
    } else url.hash = publisher?.anchor ?? "comments";
    return url.toString();
  } catch { return address; }
}

/** The adapter for an article's address, or null: then no discussion is mounted and nothing is shown. */
export function discussionProvider(url: string): string | null {
  return discussionPublisher(url)?.provider ?? null;
}

/** Who can read this provider's discussion. */
export const discussionReader = (provider: string): "server" | "phone" | "external" | undefined => publishers.find(p => p.provider === provider)?.reader as "server" | "phone" | "external" | undefined;

/** The adapter for an article's address when the backend reads it (the web's test), else null. */
export function serverDiscussion(url: string): string | null {
  const provider = discussionProvider(url);
  return provider && discussionReader(provider) === "server" ? provider : null;
}

/** The site's name as the door says it ("nsmb.com"). */
export const siteName = hostOf;

/** "No comments", "1 comment", "1,234 comments". */
export const commentsLabel = (n: number): string => (n <= 0 ? "No comments" : n === 1 ? "1 comment" : `${n.toLocaleString("en-US")} comments`);

/** A link the reader may follow: http and https only; anything else is shown as plain text. */
export function safeHref(href: string | undefined): string | null {
  if (!href) return null;
  try { const u = new URL(href.trim()); return u.protocol === "https:" || u.protocol === "http:" ? u.toString() : null; } catch { return null; }
}

/** How many characters of text the runs hold. */
export function runLength(r: Run): number {
  if (typeof r === "string") return r.length;
  if ("code" in r) return r.code.length;
  return ("i" in r ? r.i : "b" in r ? r.b : r.a).reduce((n: number, x) => n + runLength(x), 0);
}

/** A body cut to at most [max] characters of text, with an ellipsis where it was cut. */
export function capBody(body: Body, max = COMMENT_CHARS): Body {
  let left = max;
  const cut = (r: Run): Run | null => {
    if (left <= 0) return null;
    if (typeof r === "string") { if (r.length <= left) { left -= r.length; return r; } const s = cutText(r, left).trimEnd() + "…"; left = 0; return s; }
    if ("code" in r) { if (r.code.length <= left) { left -= r.code.length; return r; } const s = cutText(r.code, left) + "…"; left = 0; return { code: s }; }
    const key = "i" in r ? "i" : "b" in r ? "b" : "a";
    const inner = (r as Record<string, Run[]>)[key]!.map(cut).filter((x): x is Run => x !== null);
    return inner.length ? ({ ...r, [key]: inner } as Run) : null;
  };
  const out: Body = [];
  for (const p of body) {
    const q = p.map(cut).filter((x): x is Run => x !== null);
    if (q.length) out.push(q);
    if (left <= 0) break;
  }
  return out;
}

/**
 * Threads from comments in source order. Duplicate ids keep the first; a comment whose parent is
 * itself, missing, or part of a loop becomes a conversation of its own (a loop breaks at its
 * earliest member). Every reply, however deep, sits under its conversation in source order and
 * says whom it answers when that is not the conversation's first comment. A removed or empty
 * comment is dropped unless something readable answers it, when it keeps its place.
 */
export function threadComments(raw: readonly RawComment[]): Comment[] {
  const seen = new Set<string>();
  const list = raw.filter((c) => c.id && !seen.has(c.id) && !!seen.add(c.id));
  const at = new Map(list.map((c, i) => [c.id, i]));
  const parent = new Map<string, string>();
  for (const c of list) if (c.parent && c.parent !== c.id && at.has(c.parent)) parent.set(c.id, c.parent);

  // Resolve each path once; a cycle belongs to its earliest member in source order.
  const rootById = new Map<string, string>();
  for (const c of list) {
    if (rootById.has(c.id)) continue;
    const walked: string[] = [];
    const position = new Map<string, number>();
    let id: string | undefined = c.id;
    while (id !== undefined && !rootById.has(id) && !position.has(id)) {
      position.set(id, walked.length);
      walked.push(id);
      id = parent.get(id);
    }
    const root = id === undefined ? walked[walked.length - 1]! : rootById.get(id) ?? walked.slice(position.get(id)!).reduce((a, b) => at.get(a)! < at.get(b)! ? a : b);
    if (id !== undefined && !rootById.has(id)) parent.delete(root);
    for (const member of walked) rootById.set(member, root);
  }
  const rootOf = (id: string): string => rootById.get(id)!;

  const gone = (c: RawComment) => c.removed === true || c.hidden === true || (c.body.length === 0 && !c.quote);
  const byId = new Map(list.map((c) => [c.id, c]));
  const draw = (c: RawComment, to?: string): Comment => {
    const out: Comment = { id: c.id, by: c.by, body: capBody(c.body) };
    if (c.at !== undefined) out.at = c.at;
    if (c.score !== undefined) out.score = c.score;
    if (c.tag) out.tag = c.tag;
    if (c.quote) out.quote = { ...(c.quote.by ? { by: c.quote.by } : {}), text: cutText(c.quote.text, 600) };
    if (c.link) out.link = c.link;
    if (to) out.to = to;
    return out;
  };

  const roots: Comment[] = [];
  const replies = new Map<string, Comment[]>();
  for (const c of list) {
    const root = rootOf(c.id);
    if (c.id === root) continue;
    if (gone(c)) continue;
    const p = byId.get(parent.get(c.id)!)!;
    const to = p.id === root || gone(p) ? undefined : p.by || undefined;
    const r = draw(c, to);
    (replies.get(root) ?? replies.set(root, []).get(root)!).push(r);
  }
  for (const c of list) {
    if (rootOf(c.id) !== c.id) continue;
    const under = replies.get(c.id);
    if (gone(c)) {
      if (!under) continue;
      roots.push({ id: c.id, by: "", body: [], removed: true, ...(c.hidden ? { hidden: true as const } : {}), replies: under });
      continue;
    }
    const r = draw(c);
    if (under) r.replies = under;
    roots.push(r);
  }
  return roots;
}

/** Every readable comment, replies included (a held place for a removed one does not count). */
export const countComments = (roots: readonly Comment[]): number =>
  roots.reduce((n, r) => n + (r.removed ? 0 : 1) + (r.replies?.length ?? 0), 0);

const CURSOR = /^o:(\d{1,6})$/;
/** What an adapter found on a page: whether it had a comment section at all, whether it says comments are closed, and the comments in page order. */
export interface ParsedDiscussion { found: boolean; closed: boolean; comments: RawComment[] }

/** The offset a cursor names ("" or none: the start), or null for a cursor this never made. */
export function cursorOffset(cursor: string | undefined): number | null {
  if (!cursor) return 0;
  const m = CURSOR.exec(cursor);
  return m ? Number(m[1]) : null;
}

/**
 * One page of conversations from all of them: at most [per] and about [bytes] of JSON (never
 * fewer than one), the cursor for the next and how many conversations are left after this page. Null for a cursor that was not made here.
 */
export function pageSlice(roots: readonly Comment[], cursor: string | undefined, per = ROOTS_PER_PAGE, bytes = PAGE_BYTES): { threads: Comment[]; next?: string; left?: number } | null {
  const from = cursorOffset(cursor);
  if (from === null) return null;
  const threads: Comment[] = [];
  let size = 0;
  for (let i = from; i < roots.length && threads.length < per; i++) {
    size += JSON.stringify(roots[i]).length;
    if (threads.length && size > bytes) break;
    threads.push(roots[i]!);
  }
  const end = from + threads.length;
  return end < roots.length ? { threads, next: `o:${end}`, left: roots.length - end } : { threads };
}

/** What a request for an article's discussion comes back as, from what an adapter found on the page. Null for a cursor this never made. */
export function discussionPage(parsed: ParsedDiscussion, link: string, cursor: string | undefined, now: number): DiscussionPage | null {
  if (!parsed.found) return { state: "none", threads: [], link, fetchedAt: now };
  const roots = threadComments(parsed.comments);
  if (!roots.length) return { state: parsed.closed ? "closed" : "empty", threads: [], link, fetchedAt: now };
  const slice = pageSlice(roots, cursor);
  if (!slice) return null;
  return { state: "ok", total: countComments(roots), ...slice, link, fetchedAt: now };
}
